<template>
  <div class="flex w-72 flex-col gap-4 px-4 py-3">
    <div
      v-if="hasItemLimit"
      class="flex items-center gap-2 text-sm text-text-secondary">
      <span>{{ $t('W: Antal poster') }}</span>
      <input
        type="number"
        min="3"
        max="50"
        :value="localItemLimit"
        class="w-20 rounded border border-border-primary bg-surface px-2 py-1 text-sm text-text-primary"
        @change="onItemLimitChange" />
    </div>

    <div
      v-if="hasMetricAndPeriod"
      class="flex items-end gap-3">
      <div class="w-36">
        <UiInputSelectVee
          name="widgetSettingsMetric"
          :label="$t('W: Data')"
          :options="metricOptions"
          :modelValue="localMetric ?? ''"
          @update:model-value="onMetricChange" />
      </div>

      <div
        v-if="isPeriodGraph"
        class="w-36">
        <UiInputSelectVee
          name="widgetSettingsPeriod"
          :label="$t('W: Periode')"
          :options="periodOptions"
          :modelValue="localPeriod ?? ''"
          @update:model-value="onPeriodChange" />
      </div>
    </div>

    <template v-if="hasAnalysisFilters">
      <UiComboboxMultiVee
        name="widgetAnalysisFilterIDs"
        :label="$t('W: Analyser')"
        :modelValue="localAnalysisIDs"
        :options="analysisOptions"
        :placeholder="$t('W: Alle analyser')"
        :searchPlaceholder="$t('term.search')"
        :emptyText="$t('W: Ingen resultater')"
        @update:model-value="onAnalysisIDsChange" />

      <UiComboboxMultiVee
        v-if="isPriorityColoringEnabled"
        name="widgetTierFilterIDs"
        :label="$t('W: Tid tilbage')"
        :modelValue="localTierIDs"
        :options="tierOptions"
        :placeholder="$t('W: Alle')"
        :searchPlaceholder="$t('term.search')"
        :emptyText="$t('W: Ingen resultater')"
        @update:model-value="onTierIDsChange" />

      <UiComboboxMultiVee
        name="widgetLocationFilterIDs"
        :label="$t('W: Lokation')"
        :modelValue="localLocationIDs"
        :options="locationOptions"
        :placeholder="$t('W: Alle lokationer')"
        :searchPlaceholder="$t('term.search')"
        :emptyText="$t('W: Ingen resultater')"
        @update:model-value="onLocationIDsChange" />

      <div class="flex items-center justify-between border-t border-border-subtle pt-3">
        <button
          type="button"
          class="cursor-pointer text-sm text-action disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="!hasActiveFilters"
          @click="onClearClick">
          {{ $t('W: Ryd filtre') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { TWidgetMetric, TWidgetPeriod } from '@team-milva/lab-models/widget'

type TOption = { value: string; text: string }
type TSettingsPatch = {
  itemLimit?: number
  metric?: TWidgetMetric
  period?: TWidgetPeriod
  analysisIDs?: string[]
  tierIDs?: string[]
  locationIDs?: string[]
}

export default {
  name: 'WidgetSettingsMenu',

  emits: ['close'],

  props: {
    // Which sections to render — independent, not mutually exclusive (e.g.
    // Top Customers has both hasItemLimit and hasMetricAndPeriod).
    hasItemLimit: { type: Boolean, required: true },
    hasMetricAndPeriod: { type: Boolean, required: true },
    hasAnalysisFilters: { type: Boolean, required: true },
    isPeriodGraph: { type: Boolean, required: true },

    itemLimit: { type: Number, default: undefined },
    metric: { type: String as PropType<TWidgetMetric>, default: undefined },
    period: { type: String as PropType<TWidgetPeriod>, default: undefined },
    metricOptions: { type: Array as PropType<TOption[]>, required: true },
    periodOptions: { type: Array as PropType<TOption[]>, required: true },

    analysisIDs: { type: Array as PropType<string[]>, required: true },
    tierIDs: { type: Array as PropType<string[]>, required: true },
    locationIDs: { type: Array as PropType<string[]>, required: true },
    analysisOptions: { type: Array as PropType<TOption[]>, required: true },
    tierOptions: { type: Array as PropType<TOption[]>, required: true },
    locationOptions: { type: Array as PropType<TOption[]>, required: true },
    isPriorityColoringEnabled: { type: Boolean, required: true },

    onUpdate: {
      type: Function as PropType<(patch: TSettingsPatch) => void>,
      required: true,
    },
  },

  data() {
    return {
      // GlobalMenuHost passes props as a one-time snapshot taken when the
      // menu opens — it doesn't stay reactively linked back to the widget
      // once open. Local copies (updated optimistically on every change,
      // alongside the onUpdate() call that persists to the real widget
      // config) keep this menu's own controls in sync with what the user
      // just picked, instead of appearing to silently revert.
      localItemLimit: this.itemLimit,
      localMetric: this.metric,
      localPeriod: this.period,
      localAnalysisIDs: [...this.analysisIDs],
      localTierIDs: [...this.tierIDs],
      localLocationIDs: [...this.locationIDs],
    }
  },

  computed: {
    // Only the three filter dimensions count as "active filters" — metric/
    // period/itemLimit always hold some value (there's no off state for
    // them), so they never factor into this or the trigger's badge count.
    hasActiveFilters(): boolean {
      return (
        this.localAnalysisIDs.length > 0 ||
        this.localTierIDs.length > 0 ||
        this.localLocationIDs.length > 0
      )
    },
  },

  methods: {
    onItemLimitChange(event: Event): void {
      const value = Number((event.target as HTMLInputElement).value)
      this.localItemLimit = value
      this.onUpdate({ itemLimit: value })
    },

    onMetricChange(metric: TWidgetMetric) {
      this.localMetric = metric
      this.onUpdate({ metric })
    },

    onPeriodChange(period: TWidgetPeriod) {
      this.localPeriod = period
      this.onUpdate({ period })
    },

    onAnalysisIDsChange(ids: string[]) {
      this.localAnalysisIDs = ids
      this.onUpdate({ analysisIDs: ids })
    },

    onTierIDsChange(ids: string[]) {
      this.localTierIDs = ids
      this.onUpdate({ tierIDs: ids })
    },

    onLocationIDsChange(ids: string[]) {
      this.localLocationIDs = ids
      this.onUpdate({ locationIDs: ids })
    },

    onClearClick() {
      this.localAnalysisIDs = []
      this.localTierIDs = []
      this.localLocationIDs = []
      this.onUpdate({ analysisIDs: [], tierIDs: [], locationIDs: [] })
    },
  },
}
</script>
